<template>
  <label class="file-drop-zone"
         :class="{ 'is-dragging': isDragging, 'is-disabled': disabled }"
         @dragenter.prevent="startDragging"
         @dragover.prevent="startDragging"
         @dragleave.prevent="stopDragging"
         @drop.prevent="handleDrop"
  >
    <input ref="fileInput"
           class="file-input"
           type="file"
           :multiple="multiple"
           :accept="accept"
           :disabled="disabled"
           @change="handleSelection"
    >
    <span class="material-icons upload-icon" aria-hidden="true">upload_file</span>
    <span class="drop-zone-copy">
      <span class="drop-zone-title">{{ isDragging ? dropLabel : title }}</span>
      <span class="drop-zone-hint">{{ hint }}</span>
      <span v-if="acceptedTypes" class="accepted-types">{{ acceptedTypes }}</span>
    </span>
    <span class="choose-files-button" aria-hidden="true">{{ chooseLabel }}</span>
  </label>
</template>

<script setup>
import { ref } from 'vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  hint: {
    type: String,
    required: true
  },
  dropLabel: {
    type: String,
    required: true
  },
  chooseLabel: {
    type: String,
    required: true
  },
  acceptedTypes: {
    type: String,
    default: ''
  },
  accept: {
    type: String,
    default: ''
  },
  multiple: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['files-selected'])
const fileInput = ref(null)
const isDragging = ref(false)
let dragDepth = 0

function startDragging (event) {
  if (props.disabled || !hasFiles(event)) return
  dragDepth += event.type === 'dragenter' ? 1 : 0
  isDragging.value = true
}

function stopDragging () {
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) isDragging.value = false
}

function handleDrop (event) {
  dragDepth = 0
  isDragging.value = false
  if (props.disabled) return
  emitFiles(event.dataTransfer?.files)
}

function handleSelection (event) {
  emitFiles(event.target.files)
  event.target.value = ''
}

function emitFiles (fileList) {
  const files = Array.from(fileList ?? [])
  if (files.length > 0) emit('files-selected', files)
}

function hasFiles (event) {
  const types = Array.from(event.dataTransfer?.types ?? [])
  return types.length === 0 || types.includes('Files')
}
</script>

<style lang="scss" scoped>
.file-drop-zone {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 88px;
  padding: 16px 18px;
  border: 2px dashed #a9b2c3;
  border-radius: 10px;
  box-sizing: border-box;
  background: #f8f9fc;
  color: #263247;
  cursor: pointer;
  transition: border-color 160ms ease, background-color 160ms ease, box-shadow 160ms ease;
}

.file-drop-zone:hover,
.file-drop-zone:focus-within {
  border-color: var(--mainBlue);
  background: #f1f2fa;
  box-shadow: 0 0 0 3px rgba(41, 32, 114, 0.14);
}

.file-drop-zone.is-dragging {
  border-color: var(--mainBlue);
  border-style: solid;
  background: #e9e8f5;
  box-shadow: 0 0 0 4px rgba(41, 32, 114, 0.18);
}

.file-drop-zone.is-disabled {
  cursor: wait;
  opacity: 0.65;
}

.file-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.upload-icon {
  flex: 0 0 auto;
  font-size: 32px;
  color: var(--mainBlue);
}

.drop-zone-copy {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.drop-zone-title {
  font-size: 15px;
  font-weight: 700;
}

.drop-zone-hint,
.accepted-types {
  margin-top: 3px;
  font-size: 12px;
  color: #5f6878;
}

.choose-files-button {
  flex: 0 0 auto;
  padding: 10px 14px;
  border-radius: 6px;
  background: var(--mainBlue);
  color: white;
  font-size: 13px;
  font-weight: 700;
}

@media (max-width: 600px) {
  .file-drop-zone {
    flex-wrap: wrap;
  }

  .choose-files-button {
    width: 100%;
    box-sizing: border-box;
    text-align: center;
  }
}
</style>
